<template>
  <aside class="sidebar">
    <logo class="logo" />
    <div class="version-info">
      <span>{{ frontendVersion }}</span>
      <span>API: {{ backendVersion }}</span>
    </div>
    <nav-list class="root-nav">
      <nav-item :icon="DashboardIcon" to="/" :label="$t('navigation.dashboard.DEFAULT')" />
      <nav-item :icon="AnnounceIcon" to="/campaigns" :label="$t('navigation.campaigns.DEFAULT')" />
      <nav-item :icon="CompanyIcon" to="/companies" :label="$t('navigation.companies.DEFAULT')" />
      <nav-item :icon="LoyaltyIcon" to="/licenses" :label="$t('navigation.licenses.DEFAULT')" />
      <nav-item :icon="TourIcon" to="/tours" :label="$t('navigation.tours.DEFAULT')" />
      <nav-item :icon="BicycleIcon" to="/drivers" :label="$t('navigation.drivers.DEFAULT')" />
      <nav-item :icon="MountingIcon" to="/mountings" :label="$t('navigation.mountings.DEFAULT')">
        <nav-list inner>
          <nav-item to="/mountings" :label="$t('navigation.mountings.places')" />
          <nav-item to="/mountings-drivers" :label="$t('navigation.mountings.drivers')" />
        </nav-list>
      </nav-item>
      <nav-item :icon="ImagesIcon" to="/images" :label="$t('navigation.images.DEFAULT')">
        <nav-list inner>
          <nav-item to="/images/photos" :label="$t('navigation.images.photos.DEFAULT')" />
          <nav-item
            to="/images/verifications"
            :label="$t('navigation.images.verifications.DEFAULT')"
          />
        </nav-list>
      </nav-item>
      <nav-item
        :icon="NotificationIcon"
        to="/notifications"
        :label="$t('navigation.notifications.DEFAULT')"
      />
      <nav-item
        :icon="DataHistoryIcon"
        to="/history/general"
        :label="$t('navigation.history.DEFAULT')"
      />
      <nav-item
        :icon="VisibilityIcon"
        to="/visibility"
        :label="$t('navigation.visibility.DEFAULT')"
      >
        <nav-list inner>
          <nav-item to="/visibility/plannings" :label="$t('navigation.visibility.plannings')" />
          <nav-item to="/visibility/transfers" :label="$t('navigation.visibility.transfers')" />
          <nav-item to="/visibility/bicycles" :label="$t('navigation.visibility.bicycles')" />
          <nav-item to="/visibility/locations" :label="$t('navigation.visibility.locations')" />
          <nav-item to="/visibility/photos" :label="$t('navigation.visibility.photos')" />
        </nav-list>
      </nav-item>
      <nav-item :icon="LoyaltyIcon" to="/loyalty" :label="$t('navigation.loyalty.DEFAULT')">
        <nav-list inner>
          <nav-item to="/loyalty/interest-zones" :label="$t('navigation.loyalty.interestZones')" />
          <nav-item to="/loyalty/point-rewards" :label="$t('navigation.loyalty.rewards')" />
        </nav-list>
      </nav-item>
      <nav-item :icon="TemplateIcon" to="/templates" :label="$t('navigation.templates.DEFAULT')" />
      <nav-item
        :icon="AdvertisementIcon"
        to="/advertisements"
        :label="$t('navigation.advertisements.DEFAULT')"
      />
      <nav-item :icon="DataHistoryIcon" to="/monitoring" :label="$t('navigation.monitoring.DEFAULT')">
        <nav-list inner>
          <nav-item to="/monitoring" :label="$t('navigation.monitoring.verification')" />
        </nav-list>
      </nav-item>
      <nav-item :icon="ContentIcon" to="/content/videos" :label="$t('navigation.content.DEFAULT')">
        <nav-list inner>
          <nav-item to="/content/videos" :label="$t('navigation.content.videos')" />
          <nav-item
            to="/content/video-categories"
            :label="$t('navigation.content.video-categories')"
          />
          <nav-item to="/content/faq" :label="$t('navigation.content.faq.DEFAULT')" />
        </nav-list>
      </nav-item>
      <nav-item :icon="StatisticIcon" to="/invoices" :label="$t('navigation.invoicing.DEFAULT')" />
      <nav-item
        :icon="StatisticIcon"
        to="/additional-payments"
        :label="$t('navigation.additionalPayments.DEFAULT')"
      />
      <nav-item :icon="SettingsIcon" to="/settings" :label="$t('navigation.settings.DEFAULT')" />
      <nav-item :icon="UserIcon" to="/users" :label="$t('navigation.users.DEFAULT')" />
    </nav-list>

    <div v-if="user" class="user">
      <avatar :user="user" />
      <div class="logout-area">
        <logout />
      </div>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import Logo from '@images/logo.svg?component';
import DashboardIcon from '@images/navigation/dashboard.svg?component';
import AnnounceIcon from '@images/navigation/announce.svg?component';
import BicycleIcon from '@images/navigation/bicycle.svg?component';
import TourIcon from '@images/navigation/tour.svg?component';
import ContentIcon from '@images/navigation/content.svg?component';
import NotificationIcon from '@images/navigation/notification.svg?component';
import ImagesIcon from '@images/navigation/images.svg?component';
import MountingIcon from '@images/navigation/mounting.svg?component';
import SettingsIcon from '@images/navigation/settings.svg?component';
import UserIcon from '@images/navigation/users.svg?component';
import CompanyIcon from '@images/navigation/company.svg?component';
import LoyaltyIcon from '@images/navigation/loyalty.svg?component';
import TemplateIcon from '@images/navigation/template.svg?component';
import DataHistoryIcon from '@images/navigation/data-history.svg?component';
import VisibilityIcon from '@images/navigation/visibility.svg?component';
import AdvertisementIcon from '@images/navigation/advertisement.svg?component';
import StatisticIcon from '@images/navigation/statistic.svg?component';
import Avatar from '@utils/Avatar.vue';
import Logout from '@utils/Logout.vue';
import NavList from '@components/structure/navigation/NavList.vue';
import NavItem from '@components/structure/navigation/NavItem.vue';
import { computed, ref, onMounted } from 'vue';

import { useStore } from '@/store';
import { getAppVersion } from '@/utils/version';
import { fetchApiBase } from '@/hooks/fetch';

const store = useStore();
const user = computed(() => store?.state.auth.user);

// Frontend version from build
const frontendVersion = getAppVersion();

// Backend version from API
const backendVersion = ref('...');

onMounted(async () => {
  try {
    const response = await fetchApiBase('/version');
    const text = await response.text();
    backendVersion.value = (text === '0.0.0.0' ? 'dev' : text) || '?';
  } catch (e) {
    backendVersion.value = '?';
  }
});
</script>

<style lang="scss" scoped>
.sidebar {
  @apply fixed left-0 top-0 h-full bg-primary;
  @apply flex flex-col items-center gap-y-10;

  padding-left: 2px;
  padding-right: 2px;
  width: var(--sidebar-width);
  overflow-y: auto;

  .logo {
    @apply pt-10 pb-2;
    width: 70%;
    flex: 0 0 auto;

    :deep(path) {
      fill: white;
    }
  }

  .version-info {
    @apply flex flex-col items-center text-xs;
    color: rgba(255, 255, 255, 0.6);
    margin-top: -1.5rem;
    font-size: 0.8rem;
    line-height: 1.3;
  }
}

.user {
  @apply flex justify-between;
  @apply w-full;
  @apply mt-auto py-6;
  @apply border-t-red-120;
  @apply relative;

  border-top-width: 3px;

  padding-left: px(20);
  padding-right: px(20);

  .logout-area {
    @apply pl-6;
    @apply flex items-center;
    @apply border-l-2 border-l-inactive;

    padding-right: px(20);
  }

  &::before {
    @apply h-10 w-full;
    @apply absolute;

    content: '';
    background: linear-gradient(0, var(--red-gradient));

    left: 0;
    bottom: 100%;
  }
}
</style>
